import type { ReactNode } from 'react';
import type {
  CategoryDetail,
  DataQualityReport,
  NarrativeNumberFlagView,
  InvestmentScore,
  IrrMatrix,
  LenderSummary,
  PremiumDealAnalysis,
  Verdict,
  EvidenceCoverage,
  ExitCapReferenceView,
  BorderlineView,
  BudgetOmissionView,
} from '../api.js';
import {
  basisPoints,
  currency,
  currencyCompact,
  multiple,
  percent,
  percentFromRatio,
} from '../format.js';
import { Markdown } from './Markdown.js';
import { ChallengeButton, ChallengeLedgerCard, type ChallengeContext } from './Challenges.js';
import { ReportSections, buildSections } from './ReportSections.js';
import { ChartTable } from './ChartTable.js';
import { usePrintExpanded } from './usePrintExpanded.js';
import { categoryTable, irrMatrixTable, lenderTable } from './chart-tables.js';
import { DealIdentity, hasDealIdentity, type DealIdentityFields } from './DealIdentity.js';
import { ConstructionCard } from './ConstructionCard.js';
import { DealMechanicCard } from './DealMechanicCard.js';
import { RentPositionCard } from './RentPositionCard.js';
import {
  CategoryRadar,
  CompletenessDonut,
  HurdleBar,
  IrrHeatmapChart,
  ReturnsBullet,
  ScoreDial,
  type RadarAxis,
} from './charts.js';

// Renders the IC-memo-grade premium bundle as a NextLevel, data-viz report (report-visualization-spec
// §3). Numbers are engine-owned; prose is model-owned. Every field is treated as possibly-absent
// (workspace-isolated permissive types), so each section guards.

const VERDICT_LABEL: Record<Verdict, string> = {
  READY: 'Ready',
  ADVANCE: 'Advance',
  WORKSHOP: 'Workshop',
  RESUBMIT: 'Resubmit',
};

/**
 * `challenge` is optional so every other caller of this report — and any run rendered before the
 * challenge surface existed — keeps working unchanged: absent, the report is exactly the read-only
 * document it has always been (ADR-0038 F4).
 */
export function AnalysisReport({
  run,
  challenge,
  sources,
  identity,
  budgetOmissions = null,
  notChecked = [],
}: {
  run: PremiumDealAnalysis;
  challenge?: ChallengeContext | null;
  /**
   * Where the deal's documents came from and how cleanly they read — passed in by the ingestion view so
   * it lands INSIDE the Sources section instead of stacking above the report. That placement is the
   * point: provenance belongs with the deal, not in front of it.
   */
  sources?: ReactNode;
  /**
   * WHICH deal this is — project, address, sponsor. Lives on the RUN ROW rather than in the analysis
   * bundle (a name asserts nothing about economics, exactly as `analyses.site` is its own column and
   * not a `DealInputs` field), so the view threads it in. Optional: every other caller of this report,
   * and every run analysed before these fields travelled, renders unchanged and simply unnamed.
   */
  identity?: DealIdentityFields | null;
  /**
   * The construction-budget read's omitted lines, which live on the ingestion wrapper rather than the
   * bundle, so the view threads them in. Scope coverage (ADR-0069 A11) names an un-itemized lump with them.
   */
  budgetOmissions?: readonly BudgetOmissionView[] | null;
  /**
   * External checks the bundle lists as wired that, on this run, declined to run (#1532: Indiana's
   * property-tax cap). The decline lives on the ingestion wrapper, so the view threads it in; without
   * it the Outside checks card would mark the check `checked`.
   */
  notChecked?: readonly string[];
}) {
  const score = run.scorecard?.score;
  const spine = run.deterministic?.spine ?? null;
  const lender = run.lender ?? run.deterministic?.lender ?? null;
  const matrix = run.sensitivity?.matrix ?? run.deterministic?.sensitivity?.matrix ?? null;
  const returns = spine?.returns ?? null;
  const dq = run.deterministic?.dataQuality ?? null;
  // Present only when a construction budget was extracted from the package — 9 of 81 stored runs at
  // the time of writing, all of them ingestion-path deals that shipped an estimate. Absent ⇒ the
  // section drops out entirely (buildSections), rather than rendering an empty Construction tab.
  const construction = run.deterministic?.construction ?? null;
  const mechanic = run.deterministic?.dealMechanic ?? null;
  // Only meaningful when `mechanic` is absent — it is the stated reason, and it is what stops an
  // absent lever panel reading as "this deal has no levers".
  const classGap = run.deterministic?.classUnavailable ?? null;
  // Absent whenever the package carried no rent comparables — which is most of them. The section drops
  // out rather than rendering an empty panel; the Scorecard prompt is where the absence is NAMED.
  const rentPos = run.deterministic?.rentPosition ?? null;
  // The reason a rent position could not be established, when there is one. Rendered INSTEAD of
  // silence — see `RentCheckNotRun`.
  const rentGap = rentPos ? null : (run.deterministic?.rentPositionUnavailable ?? null);

  const isLIHTC = lender?.isLIHTC === true;

  // Modules that were asked to run and threw. Per-module isolation (PRD §NFR) means the run survives —
  // but an absent section must SAY it is absent, or isolation has only traded a loud failure for a
  // silent one. `sensitivity` and `lender` still render their engine-computed figures, so their notice
  // sits INSIDE the section; `sponsor`/`icMemo` are prose-only, so a double failure would otherwise
  // delete the tab entirely.
  const failed = new Set((run.moduleFailures ?? []).map((f) => f.module));
  const sponsorSectionFailures = ['sponsor', 'icMemo'].filter((m) => failed.has(m));

  // Whether the deal is named decides the page's heading structure, so it is computed once here and
  // read in both places: the identity block owns the h1 when there is one, and the recommendation
  // takes it otherwise. Narrowed to a non-null `identity` so the render below needs no second guard.
  const named = identity != null && hasDealIdentity(identity);

  // One section per question a reader actually arrives with — "is it good?", "what drove that?", "what
  // breaks it?", "will it finance?", "who's behind it?", "where did the numbers come from?", "what does
  // anyone disagree with?" — rather than one scroll containing all seven answers in sequence.
  const sections = buildSections([
    {
      id: 'overview',
      label: 'Overview',
      content: (
        <>
          <DealHeadline
            score={score}
            model={run.scorecard?.model}
            titled={named}
            {...(run.scoringStandardVersion ? { standardVersion: run.scoringStandardVersion } : {})}
          />
          <Hero
            score={score}
            {...(run.evidenceCoverage ? { evidence: run.evidenceCoverage } : {})}
          />
          {isLIHTC && <LihtcAssumption />}
          <MetricTiles
            spine={spine}
            lender={lender}
            exitCapReference={run.deterministic?.exitCapReference ?? null}
          />
          <Narrative title="Investment rationale" body={run.scorecard?.narrative} />
        </>
      ),
    },
    {
      id: 'findings',
      label: 'Findings',
      content: score ? <CategoryBreakdown score={score} challenge={challenge ?? null} /> : null,
    },
    {
      id: 'sensitivity',
      label: 'Sensitivity',
      content:
        matrix || returns || rentPos || rentGap ? (
          <>
            {rentPos && <RentPositionCard position={rentPos} />}
            {rentGap && <RentCheckNotRun reason={rentGap} />}
            {(matrix || returns) && <SensitivityCard matrix={matrix} returns={returns} />}
            <Narrative title="What moves the return" body={run.sensitivity?.narrative} />
            {failed.has('sensitivity') && <ModuleUnavailable modules={['sensitivity']} />}
          </>
        ) : null,
    },
    {
      id: 'lender',
      label: 'Lender',
      content: lender ? (
        <>
          <LenderCard lender={lender} narrative={run.lender?.narrative} />
          {failed.has('lender') && <ModuleUnavailable modules={['lender']} />}
        </>
      ) : null,
    },
    {
      id: 'construction',
      label: 'Construction',
      /*
        The DECOMPOSITION is deterministic and has always rendered here. The NARRATIVE is the
        model-judged half, and until `runConstruction` was wired into the premium run it was never
        produced — a named module that never ran (the ADR-0043 lie). Rendered on the same rule as
        Lender: the card, then the narrative, then an explicit note if the module was asked and threw.
      */
      /*
        ⚠️ `failed.has(...)` IS PART OF THE CONDITION, not just the body. Without it a run where the
        decomposition is absent AND the narrative threw drops the tab entirely — the report looks
        complete while a section it was asked for silently vanished. That is the exact defect the
        Sponsor section carries its own note about; caught here by the test below, not by review.
      */
      content:
        construction || run.construction || failed.has('construction') ? (
          <>
            {construction && (
              <ConstructionCard
                construction={construction}
                scopeCoverage={run.deterministic?.scopeCoverage ?? null}
                omissions={budgetOmissions}
              />
            )}
            {run.construction?.narrative && (
              <Narrative title="Construction risk" body={run.construction.narrative} />
            )}
            {failed.has('construction') && <ModuleUnavailable modules={['construction']} />}
          </>
        ) : null,
    },
    {
      // Placed after Sensitivity/Lender and before Sponsor deliberately: the reader has just been
      // shown what the return IS and what moves it, and this is the answer to "so what do I change?".
      id: 'levers',
      label: 'Levers',
      content:
        mechanic || (classGap && classGap.length > 0) ? (
          <DealMechanicCard mechanic={mechanic} classUnavailable={classGap} />
        ) : null,
    },
    {
      id: 'sponsor',
      label: 'Sponsor & memo',
      content:
        run.sponsor || run.icMemo || sponsorSectionFailures.length > 0 ? (
          <>
            {run.sponsor && (
              <Narrative
                title={`Sponsor & team risk${
                  run.sponsor.score?.score !== undefined ? ` — ${run.sponsor.score.score}/20` : ''
                }`}
                body={run.sponsor.narrative}
              />
            )}
            {run.icMemo && (
              <Narrative
                title={`IC memorandum${tierSuffix(
                  run.icMemo.recommendationTierLabel,
                  run.icMemo.recommendationTier,
                )}`}
                body={run.icMemo.narrative}
              />
            )}
            {sponsorSectionFailures.length > 0 && (
              <ModuleUnavailable modules={sponsorSectionFailures} />
            )}
          </>
        ) : null,
    },
    {
      id: 'sources',
      label: 'Sources',
      content:
        sources || dq || run.externalChecks ? (
          <>
            {sources}
            {run.externalChecks && (
              <ExternalChecksCard ran={run.externalChecks} notChecked={notChecked} />
            )}
            {dq && <DataQualityCard dq={dq} narrativeFlags={run.narrativeNumbers ?? []} />}
          </>
        ) : null,
    },
    {
      id: 'challenges',
      label: 'Challenges',
      ...(challenge ? { badge: challenge.ledger.openCount } : {}),
      content: challenge ? <ChallengeLedgerCard ctx={challenge} /> : null,
    },
  ]);

  return (
    <div className="report">
      {/*
        OUTSIDE the section shell, deliberately. The sections are seven views of one deal, and the
        deal's name is true of all seven — put inside Overview it would vanish the moment a reader
        opened Findings, and the printed export (which expands every section) would carry its title
        buried in the middle of the first one.
      */}
      {named && <DealIdentity fields={identity} />}
      <ReportSections sections={sections} />
    </div>
  );
}

/**
 * The IC recommendation shown beside the memo. Prefers `recommendationTierLabel` — canon's exact
 * wording, carried on the run — so the SPA never keeps its own copy of canon's vocabulary.
 *
 * The enum fallback is for runs analysed before that field existed. It cannot reproduce canon's wording
 * (`WORKSHOP_CONDITIONAL_HOLD` has no way back to "WORKSHOP / CONDITIONAL HOLD"), which is the point:
 * historical runs render as they were written rather than being retro-labelled with today's standard.
 */
function tierSuffix(label: string | undefined, tier: string | undefined): string {
  if (label) return ` — ${label}`;
  if (!tier) return '';
  return ` — ${tier.replace(/_/g, ' ').toLowerCase()}`;
}

// The single most consequential assumption in the underwrite: `isLIHTC` exempts the deal from the Value
// Creation Gate, so a thin development spread does NOT cap Financial Underwriting or apply the structural
// penalty — worth ~25 points of the 0–100 score. When the model inferred it from the documents, that swing
// rides on a boolean the reader should see and confirm. This states the engine's behaviour as fact and
// invites verification — it never accuses the extraction of being wrong.
function LihtcAssumption() {
  return (
    <section className="assumption" role="note">
      <span className="assumption__tag">Assumption</span>
      <p className="assumption__body">
        Underwritten as <strong>LIHTC / affordable</strong>, so the Value Creation Gate is{' '}
        <strong>exempt</strong> — the development-spread structural penalty and the Financial
        Underwriting cap do not apply, which lifts the score materially.{' '}
        <strong>Confirm the program type:</strong> the same deal underwritten market-rate, with a
        thin development spread, would score lower.
      </p>
    </section>
  );
}

/**
 * What a recommendation MEANS, in the words a client would use. Canon's voice is adult-to-adult, plain
 * and grounded, systems-not-heroes — and canon §4.5 is explicit that the conclusion is "a recommendation,
 * never a verdict", so each line names the next move rather than passing sentence on the deal.
 *
 * Exported for a direct test: this is the single most-read sentence in the product, and it is the one
 * place the score turns into something a person can act on.
 */
export function recommendationMeaning(v: Verdict | undefined, score?: InvestmentScore): string {
  // A structural flag is the single largest movement in the score, and naming it is the difference
  // between "it doesn't clear the floors" — true of a great many deals — and the reader understanding
  // that ONE gap produced most of the gap. The penalty is on the bundle; the specific figure is not,
  // so this names the mechanism and its cost and leaves the arithmetic to the findings.
  // ⚠️ `structuralPenalty` is NEGATIVE: the engine computes `categoryTotal + structuralPenalty`, and
  // the composition note below renders it only when `< 0`. The first version of this guarded on
  // `> 0`, which can never be true — the figure silently never appeared, and the test passed because
  // its fixture used a positive 15 that production does not produce. Read the magnitude.
  const penalty = score?.structural === true ? score.structuralPenalty : undefined;
  const points = typeof penalty === 'number' ? Math.abs(penalty) : 0;
  const flagged =
    points > 0
      ? ` A structural flag applied, costing ${points} points.`
      : score?.structural === true
        ? ' A structural flag applied.'
        : '';

  /*
    🔴 A CAPPED RECOMMENDATION SAYS WHY (ADR-0039). An 86 reads ADVANCE when the structural ceiling
    holds it below READY, and a reader who knows the bands sees the score and the headline disagree —
    and concludes the tool is wrong (`services/src/entitlements.ts`). The engine records the band the
    score alone earned; this is the sentence that makes the two agree on the page.
  */
  const cappedFrom = score?.recommendationCappedFrom;
  const capped =
    cappedFrom && v && cappedFrom !== v
      ? ` On the score alone this reaches ${cappedFrom}; a structural flag holds the recommendation at ${v} until it is resolved.`
      : '';

  switch (v) {
    case 'READY':
      return 'Clears every underwriting floor as presented. Take it to committee.';
    case 'ADVANCE':
      return `Worth advancing, with the conditions in the findings below.${flagged}${capped}`;
    case 'WORKSHOP':
      return `The economics don’t hold yet, but the gaps are workable. Rework and re-run it.${flagged}`;
    case 'RESUBMIT':
      return `It doesn’t clear the floors as underwritten.${flagged} Resubmit once the inputs change.`;
    default:
      return 'This deal has not been scored yet.';
  }
}

/**
 * The first screen — the deal at a glance, built to be looked at rather than read. The recommendation
 * carries the headline at canon’s H1 scale (Montserrat ExtraBold, 32–52px, tight tracking) and the score
 * is a hero figure beside it, because "a single current value" is a stat tile, never a chart.
 *
 * Brand proportion is canon’s, not taste: cream ~50% · navy ~30% · charcoal ~14% · GOLD ~6%. So the band
 * is a navy field with cream type, and gold appears as one hairline rule — an accent, never a field.
 */
function DealHeadline({
  score,
  model,
  standardVersion,
  titled = false,
}: {
  score?: InvestmentScore;
  model?: string;
  standardVersion?: string;
  /**
   * Whether a `DealIdentity` heading already titles this report.
   *
   * Only the HEADING LEVEL moves. `.dh__verdict` keeps canon §07's H1 scale either way — the type is
   * class-driven, and its size was #367's ruled audit finding, not an artifact of the tag. What the
   * tag decides is document structure: a titled report must not carry two h1s, and an untitled one
   * must not start at h2.
   */
  titled?: boolean;
}) {
  const verdict = score?.recommendation ?? score?.verdict;
  const Headline = titled ? 'h2' : 'h1';
  const margin = marginLine(score?.recommendationMargin);
  return (
    <>
      <section className={`dh dh--${verdictKey(verdict)}`}>
        <div className="dh__main">
          <p className="dh__eyebrow">Investment recommendation</p>
          <Headline className="dh__verdict">
            {verdict ? VERDICT_LABEL[verdict] : 'Pending'}
          </Headline>
          <p className="dh__meaning">{recommendationMeaning(verdict, score)}</p>
          {margin && <p className="dh__margin">{margin}</p>}
        </div>
        <div className="dh__score" aria-label="Investment score">
          <span className="dh__figure">{score?.overallScore ?? '—'}</span>
          <span className="dh__outof">/100</span>
        </div>
        {/*
          Canon v2.7.16 step 11: the engine-composed potential, shown only when it is above the score —
          a potential equal to the score says nothing a reader needs. When it rests on accepted AS_PRICED
          relief, the reader is told what it assumes.
        */}
        {score?.potentialScore !== undefined &&
          score.overallScore !== undefined &&
          score.potentialScore > score.overallScore && (
            <p className="dh__potential">
              Potential {score.potentialScore}/100 if the closable gaps are closed
              {score.potentialRelief?.accepted
                ? ', assuming the development spread clears 50 bps on the quoted comps'
                : ''}
              .
            </p>
          )}
        <p className="dh__fine">
          {model ? `Judged by ${model}` : 'Deterministic engine'}
          {standardVersion ? ` · scoring standard ${standardVersion}` : ''}
        </p>
      </section>
      {score?.borderline && <BorderlineNote borderline={score.borderline} current={verdict} />}
    </>
  );
}

/** A points figure as a reader says it: "1 point", "3 points", "2.5 points". */
function pointsPhrase(n: number): string {
  const abs = Math.abs(n);
  return `${abs} ${abs === 1 ? 'point' : 'points'}`;
}

/**
 * ADR-0069 N20 — the margin line under the headline: how far the score sits from the line that decides
 * its recommendation.
 *
 * `null` when the engine recorded no margin (a scorecard stored before N20 — the browser does not
 * compute one) and when the recommendation was HELD by the structural ceiling: the meaning sentence
 * above already says what holds it and what the score alone reached, and a margin to READY's line would
 * describe a band the reader was not given.
 */
export function marginLine(margin: InvestmentScore['recommendationMargin']): string | null {
  if (!margin || margin.kind !== 'margin') return null;
  const band = VERDICT_LABEL[margin.lineBand];
  if (margin.points === 0) return `The score sits exactly on the ${band} line (${margin.line}).`;
  return `${pointsPhrase(margin.points)} ${margin.side} the ${band} line (${margin.line}).`;
}

/**
 * ADR-0069 N20 — the amber "borderline / tier-fragile" note (canon `1_ScoringEngine` A27:C30).
 *
 * A note on CONFIDENCE, never a change: the score and the recommendation above are exactly what they
 * would be without it, and the note says so. The engine only sends a band that would move something a
 * reader is shown, so every sentence here names what that is.
 *
 * The tier-boundary flag is paid whole and never reaches a free reader (`entitlements.ts`), so there
 * is no free variant of its sentence. A flag missing a figure renders no tier sentence rather than a
 * guessed one.
 */
export function BorderlineNote({
  borderline,
  current,
}: {
  borderline: BorderlineView;
  /** The presented recommendation, so a crossing that changes nothing says "stay". */
  current?: Verdict | undefined;
}) {
  const line = borderline.recommendationLine;
  const tier = borderline.tierBoundary
    ? tierBoundarySentence(borderline.tierBoundary, current)
    : null;
  if (!line && !tier) return null;
  return (
    <section className="borderline-note" aria-label="Borderline recommendation">
      <p className="borderline-note__title">
        {line && tier ? 'Borderline and tier-fragile' : line ? 'Borderline' : 'Tier-fragile'}
      </p>
      {line && <p>{recommendationLineSentence(line)}</p>}
      {tier && <p>{tier}</p>}
      <p className="borderline-note__fine">
        This is a note on how close the call is. It does not change the score or the recommendation.
      </p>
    </section>
  );
}

function recommendationLineSentence(
  line: NonNullable<BorderlineView['recommendationLine']>,
): string {
  const across = VERDICT_LABEL[line.recommendationIfCrossed];
  const where =
    line.distance === 0
      ? `The score sits exactly on the ${line.line} line`
      : `The score is ${pointsPhrase(line.distance)} ${line.distance > 0 ? 'above' : 'below'} the ${line.line} line`;
  return `${where}, so a small change to any input could make this ${across}.`;
}

function tierBoundarySentence(
  tier: NonNullable<BorderlineView['tierBoundary']>,
  current: Verdict | undefined,
): string | null {
  if (
    typeof tier.boundaryBps !== 'number' ||
    typeof tier.devSpreadBps !== 'number' ||
    typeof tier.distanceBps !== 'number' ||
    typeof tier.swingPoints !== 'number'
  ) {
    return null;
  }
  const ceiling =
    tier.structuralCeiling === 'sets'
      ? ' Below it, the structural flag would also hold the recommendation at Advance or lower.'
      : tier.structuralCeiling === 'clears'
        ? ' At or above it, the structural flag would come off.'
        : '';
  const across = tier.recommendationIfCrossed;
  const recPhrase = across
    ? across === current
      ? `the recommendation would stay ${VERDICT_LABEL[across]}`
      : `the recommendation would be ${VERDICT_LABEL[across]}`
    : undefined;
  const b = tier.boundaryBps;
  const side = tier.distanceBps >= 0 ? 'above' : 'below';
  const position =
    tier.distanceBps === 0
      ? `The development spread (${tier.devSpreadBps} bps) sits exactly on the ${b} bps tier line.`
      : `The development spread (${tier.devSpreadBps} bps) is ${Math.abs(tier.distanceBps)} bps ${side} the ${b} bps tier line.`;
  const move = tier.distanceBps >= 0 ? `Dropping below it` : `Reaching it`;
  const effect =
    tier.swingPoints === 0
      ? `${move} would not move the score`
      : `${move} would ${tier.swingPoints < 0 ? 'take' : 'add'} ${pointsPhrase(tier.swingPoints)} ${tier.swingPoints < 0 ? 'off' : 'to'} the score${typeof tier.scoreIfCrossed === 'number' ? ` (${tier.scoreIfCrossed})` : ''}`;
  return `${position} ${effect}${recPhrase ? `, and ${recPhrase}` : ''}.${ceiling}`;
}

function verdictKey(v: Verdict | undefined): string {
  return (v ?? 'workshop').toLowerCase();
}

const CATEGORIES: {
  key: keyof NonNullable<InvestmentScore['categoryScores']>;
  label: string;
  short: string;
  max: number;
}[] = [
  { key: 'locationMarket', label: 'Location & market', short: 'Location', max: 20 },
  { key: 'financialUnderwriting', label: 'Financial underwriting', short: 'Financial', max: 20 },
  { key: 'sponsorExecution', label: 'Sponsor & execution', short: 'Sponsor', max: 20 },
  { key: 'capitalStack', label: 'Capital stack', short: 'Capital', max: 20 },
  { key: 'productDesign', label: 'Product & design', short: 'Product', max: 10 },
  { key: 'exitStrategy', label: 'Exit strategy', short: 'Exit', max: 10 },
];

// A category is EITHER a bare number (historical analyses) or a `CategoryDetail` object (current). These
// read the applied score and the detail from either shape, so old and new analyses both render.
function catScore(v: number | CategoryDetail | undefined): number | undefined {
  if (v === undefined) return undefined;
  return typeof v === 'number' ? v : v.score;
}
function catDetail(v: number | CategoryDetail | undefined): CategoryDetail | undefined {
  return v !== undefined && typeof v === 'object' ? v : undefined;
}

/**
 * How much of the score rests on something.
 *
 * Two REAL deals scored identically in five of six categories on 2026-07-29 — 43 of Hazel Street's 58
 * points carried no deal-specific information, on a deal clearing ADVANCE by three. The number alone
 * cannot say that. This says it beside the number, and changes no arithmetic: resolving the ambiguity
 * is a standard question (#448), making it visible is not.
 *
 * Deliberately NOT a percentage-of-a-percentage. "40 of 100 points rest on evidence" is a fact a reader
 * can check against the category table below it; "40% confidence" would be a claim we cannot support.
 */
function CoverageLine({
  coverage,
  score,
}: {
  coverage: { evidenced: number; unevidenced: number; untracked: number; total: number };
  score?: number;
}) {
  const { evidenced, unevidenced, untracked, total } = coverage;
  return (
    <div className="coverage">
      <div className="coverage__head">
        <span className="coverage__label">Evidence-backed weight</span>
        <span className="coverage__figure">
          {evidenced}
          <span className="coverage__of">/{total}</span>
        </span>
      </div>
      <div className="coverage__bar" aria-hidden="true">
        <i className="coverage__seg coverage__seg--yes" style={{ width: `${evidenced}%` }} />
        <i className="coverage__seg coverage__seg--no" style={{ width: `${unevidenced}%` }} />
        <i className="coverage__seg coverage__seg--unk" style={{ width: `${untracked}%` }} />
      </div>
      <p className="coverage__note">
        {evidenced} of {total} points rest on evidence in the record
        {score !== undefined && evidenced > 0 ? (
          <> — the score of {score} is carried by that weight</>
        ) : null}
        .{' '}
        {unevidenced > 0 ? <>{unevidenced} sit at a default because nothing grounded them</> : null}
        {unevidenced > 0 && untracked > 0 ? '; ' : null}
        {untracked > 0 ? (
          <>
            {untracked} are not tracked (Sponsor &amp; Execution is composed differently and carries
            no basis audit)
          </>
        ) : null}
        .
      </p>
    </div>
  );
}

// §3.1 score dial + §3.2 category radar, paired.
//
// This card does NOT restate the verdict. `DealHeadline` sits directly above it and is the client
// headline — recommendation, score, and the model/standard provenance line. This card used to repeat all
// three, so the first screen said "Advance" twice, "68" three times, and named the model twice. A report
// a client reads states its conclusion once, with authority; the repetition read as unfinished.
// Its job here is the composition BEHIND that headline: where the score came from, across the six
// categories.
function Hero({ score, evidence }: { score?: InvestmentScore; evidence?: EvidenceCoverage }) {
  // Recommendation-first (canon §4.5); fall back to the historical `verdict` alias so pre-drop
  // stored analyses still render. New analyses carry only `recommendation` (ADR-0035 §9).
  const verdict = score?.recommendation ?? score?.verdict;
  const cs = score?.categoryScores ?? {};
  const axes: RadarAxis[] = CATEGORIES.map((c) => ({
    key: c.key,
    label: c.short,
    value: catScore(cs[c.key]),
    max: c.max,
  }));
  const hasCategories = CATEGORIES.some((c) => catScore(cs[c.key]) !== undefined);
  // Computed SERVER-SIDE and sent, not derived here. The rule for which validation outcomes count as
  // evidence is policy, and policy lives in the engine — duplicating it in the browser is how the two
  // drift. (The SPA has no workspace dependencies by design; it renders what the API computed.)
  const coverage = evidence ?? null;

  return (
    <section className={`hero hero--${verdictKey(verdict)}`}>
      <div className="hero__head">
        <h3 className="hero__title">How the score is composed</h3>
        <span className="hero__caption">Six categories, weighted to 100</span>
      </div>
      <div className="hero__viz">
        <div className="hero__dial">
          <ScoreDial
            score={score?.overallScore}
            verdict={verdict}
            structural={score?.structural}
            showVerdict={false}
          />
        </div>
        {hasCategories && (
          <div className="hero__radar">
            <CategoryRadar axes={axes} />
          </div>
        )}
      </div>
      {coverage && <CoverageLine coverage={coverage} score={score?.overallScore} />}
      {score && hasCategories && <ChartTable {...categoryTable(score, CATEGORIES)} />}
      {score?.structuralPenalty !== undefined && score.structuralPenalty < 0 && (
        <p className="card__note hero__note">
          Structural penalty {score.structuralPenalty} from the dev-spread value gate
          {score.rawFinancialUnderwriting !== undefined &&
            ` (financial underwriting capped from ${score.rawFinancialUnderwriting})`}
          .
        </p>
      )}
    </section>
  );
}

/**
 * How a `fixability` value renders — its human label and its chip tone (the CSS modifier).
 *
 * ⚠️ TOLERANT OF BOTH VOCABULARIES, DELIBERATELY. The engine now emits canon's `FIXABLE|STRUCTURAL|N/A`
 * (ADR-0035 §9); every analysis persisted BEFORE that carries the old `fixable|partial|structural`, and
 * `analyses.result` is a stored jsonb bundle — historical runs are never rewritten. The API→SPA wire is
 * untyped, so a value the map doesn't know renders as an unstyled, shouty raw string rather than failing
 * a typecheck. That is exactly how #301 silently broke the radar in #302: an engine output-shape change
 * with no consumer trace.
 *
 * Tones stay on the ORIGINAL three CSS modifiers — canon changed the vocabulary, not the palette.
 * `N/A` reuses the neutral `partial` tone, which is what it replaced.
 */
const FIXABILITY_VIEW: Record<string, { label: string; tone: string }> = {
  // Canon's ruled set (current).
  FIXABLE: { label: 'Fixable', tone: 'fixable' },
  STRUCTURAL: { label: 'Structural', tone: 'structural' },
  'N/A': { label: 'Not applicable', tone: 'partial' },
  // Historical — analyses persisted before ADR-0035. Kept renderable, not migrated.
  fixable: { label: 'Fixable', tone: 'fixable' },
  partial: { label: 'Partly fixable', tone: 'partial' },
  structural: { label: 'Structural', tone: 'structural' },
};

// §3.2b — the per-category data-gap signal (engine ADR-0031). For each category the model judged, show
// how fixable the gap is and the path toward its potential ceiling — the "so what" behind a bare
// sub-score. Renders only when the analysis carries detail, so historical bare-number analyses (which
// have no detail) simply omit this section rather than showing an empty one.
function CategoryBreakdown({
  score,
  challenge,
}: {
  score: InvestmentScore;
  challenge: ChallengeContext | null;
}) {
  const cs = score.categoryScores ?? {};
  const rows = CATEGORIES.map((c) => ({ c, detail: catDetail(cs[c.key]) })).filter(
    (r): r is { c: (typeof CATEGORIES)[number]; detail: CategoryDetail } => r.detail !== undefined,
  );
  if (rows.length === 0) return null;

  return (
    <section className="card catbreak">
      <h3 className="card__title">Category breakdown</h3>
      <ul className="catbreak__list">
        {rows.map(({ c, detail }) => (
          <CategoryRow key={c.key} c={c} detail={detail} challenge={challenge} />
        ))}
      </ul>
    </section>
  );
}

/**
 * One category row.
 *
 * Its own component ONLY so it can hold a hook: `usePrintExpanded` forces the disclosure open while
 * printing, and a hook cannot be called inside the `.map()` above. Chad's first UAT pass caught what
 * its absence costs — the exported PDF carried six chevrons and blank pages where the Findings should
 * be, and the PDF is the artifact a client forwards.
 */
function CategoryRow({
  c,
  detail,
  challenge,
}: {
  c: (typeof CATEGORIES)[number];
  detail: CategoryDetail;
  challenge: ChallengeContext | null;
}) {
  const whyRef = usePrintExpanded();
  const showCeiling = detail.potentialScore !== undefined && detail.potentialScore > detail.score;
  return (
    <li className="catbreak__row">
      {/* The header answers "where can this move" on its own; the reasoning opens on
                  demand. Six categories rendered inline cost six screens to read past. */}
      <details className="catbreak__why" ref={whyRef}>
        <summary>
          <span className="catbreak__caret" aria-hidden="true" />
          <span className="catbreak__label">{c.label}</span>
          <span className="catbreak__bar" aria-hidden="true">
            <i style={{ width: `${Math.round((detail.score / c.max) * 100)}%` }} />
          </span>
          <span className="catbreak__score">
            {detail.score}
            {showCeiling && <span className="catbreak__ceiling"> → {detail.potentialScore}</span>}
            <span className="catbreak__max">/{c.max}</span>
          </span>
          {detail.fixability &&
            (() => {
              // Unknown value → neutral tone + the raw string, never an unstyled shouty chip.
              const view = FIXABILITY_VIEW[detail.fixability] ?? {
                label: detail.fixability,
                tone: 'partial',
              };
              return <span className={`chip chip--${view.tone}`}>{view.label}</span>;
            })()}
        </summary>
        {/* What we found comes BEFORE what would fix it. Reversed, a reader meets the remedy
                    for a problem they have not been told about yet. */}
        {detail.notes && <p className="catbreak__notes">{detail.notes}</p>}
        {detail.pathToImprovement && <p className="catbreak__path">{detail.pathToImprovement}</p>}
      </details>
      {/* R1 — the affordance belongs to the FINDING, not to the report. Deliberately OUTSIDE
                  the disclosure: challenging a finding must not cost an extra interaction. */}
      {challenge && <ChallengeButton ctx={challenge} target={c.key} label={c.label} />}
    </li>
  );
}

interface Tile {
  label: string;
  value: string;
  flag?: 'pass' | 'fail';
  hurdle?: { value: number | null | undefined; hurdle: number | null | undefined };
  /**
   * The engine's metric key, where this tile has one — how a WITHHELD metric is matched to its tile.
   *
   * Only set on tiles that can be withheld today. A tile without one simply never matches, which is
   * the honest default: adding a key here claims the engine can withhold that metric.
   */
  metricKey?: string;
  /**
   * #1575 — the pass/fail rests on a replacement reserve sized on a unit count the documents contradict.
   * The number stands (the withhold never raises it); the verdict is marked unverified beside it.
   */
  unverified?: boolean;
}

// §3.5 — the 10-tile KPI grid, with a micro hurdle-bullet under the metrics that have a hurdle (DSCR
// vs floor, LTC vs cap). Pass/fail accent border + pill where the lender check applies.
function MetricTiles({
  spine,
  lender,
  exitCapReference = null,
}: {
  spine: NonNullable<PremiumDealAnalysis['deterministic']>['spine'];
  lender: LenderSummary | null;
  /** ADR-0069 N20 — shown under the Exit value tile when the package states no exit cap. */
  exitCapReference?: ExitCapReferenceView | null;
}) {
  const returns = spine?.returns ?? null;
  const dscrFlag = passFlag(lender?.dscrPass);
  const ltcFlag = passFlag(lender?.ltcPass);
  const tiles: Tile[] = [
    {
      label: 'DSCR',
      value: multiple(spine?.dscr?.value, 2),
      flag: dscrFlag,
      hurdle: { value: spine?.dscr?.value, hurdle: lender?.dscrFloor },
      unverified: lender?.dscrReserveWithheld === true && dscrFlag !== undefined,
    },
    { label: 'Yield on cost', value: percent(spine?.yieldOnCostPct, 2) },
    { label: 'Project IRR', value: percentFromRatio(returns?.projectIRR, 1) },
    { label: 'Equity multiple', value: multiple(returns?.equityMultiple, 2) },
    { label: 'Dev spread', value: basisPoints(spine?.devSpreadBps) },
    { label: 'Cost / door', value: currency(spine?.costPerDoor), metricKey: 'costPerDoor' },
    { label: 'Exit value', value: currencyCompact(spine?.exitValue) },
    {
      label: 'Loan-to-cost',
      value: percentFromRatio(spine?.ltc, 1),
      flag: ltcFlag,
      hurdle: { value: spine?.ltc, hurdle: lender?.ltcCap },
    },
    { label: 'Debt yield', value: percentFromRatio(spine?.debtYield, 2) },
    { label: 'Refi LTV', value: percentFromRatio(spine?.refiLTV, 1) },
  ];
  /*
    🔴 WITHHELD IS NOT MISSING, AND THE REPORT HAS TO SAY WHICH. A metric the engine refused to compute
    arrives as `null` exactly like one whose inputs were absent, so without this the tile renders an
    em-dash and a reader concludes the package was incomplete — when what actually happened is that the
    document stated two different values and the engine declined to divide by either.

    `self-consistency.ts`: "Never silence — a finding that suppresses another finding without saying so
    is its own kind of dishonesty." A tooltip would not discharge that; the reason is rendered.
  */
  const withheld = new Map(
    (spine?.withheld ?? [])
      .filter((w): w is { key: string; field?: string; reason?: string } => !!w?.key)
      .map((w) => [w.key, w]),
  );

  // Ten equal tiles is not a KPI row, it is a table with the labels moved. Four lead — the numbers a
  // client asks about first — and the remaining six stay available at a smaller weight, so the headline
  // reads at a glance without hiding anything the underwriting depends on.
  const LEAD = 4;
  return (
    <>
      <section className="tiles tiles--lead">
        {tiles.slice(0, LEAD).map((t) => (
          <div className={`tile tile--lead${t.flag ? ` tile--${t.flag}` : ''}`} key={t.label}>
            <span className="tile__value">{t.value}</span>
            <span className="tile__label">
              {t.label}
              {t.flag && <span className={`tile__flag tile__flag--${t.flag}`}>{t.flag}</span>}
              {t.metricKey !== undefined && withheld.has(t.metricKey) && (
                <span className="tile__flag tile__flag--withheld">withheld</span>
              )}
              {t.unverified && <span className="tile__flag tile__flag--withheld">unverified</span>}
            </span>
            {t.hurdle && t.hurdle.hurdle != null && (
              <HurdleBar value={t.hurdle.value} hurdle={t.hurdle.hurdle} pass={t.flag === 'pass'} />
            )}
          </div>
        ))}
      </section>
      {returns?.loanFromStatedEquity === true && (
        <p className="muted">Levered returns size the loan from total cost less stated equity.</p>
      )}
      <section className="tiles">
        {tiles.slice(LEAD).map((t) => (
          <div className={`tile${t.flag ? ` tile--${t.flag}` : ''}`} key={t.label}>
            <span className="tile__value">{t.value}</span>
            <span className="tile__label">
              {t.label}
              {t.flag && <span className={`tile__flag tile__flag--${t.flag}`}>{t.flag}</span>}
              {t.metricKey !== undefined && withheld.has(t.metricKey) && (
                <span className="tile__flag tile__flag--withheld">withheld</span>
              )}
              {t.unverified && <span className="tile__flag tile__flag--withheld">unverified</span>}
            </span>
            {t.hurdle && t.hurdle.hurdle != null && (
              <HurdleBar value={t.hurdle.value} hurdle={t.hurdle.hurdle} pass={t.flag === 'pass'} />
            )}
          </div>
        ))}
      </section>
      {exitCapReference && <ExitValueReferenceNote reference={exitCapReference} />}
      {withheld.size > 0 && (
        <section className="tiles__withheld">
          {[...withheld.values()].map((w) => (
            <p key={w.key}>
              <strong>{TILE_LABELS[w.key] ?? w.key} was withheld.</strong>{' '}
              {w.reason ??
                'The document states more than one value for one of its inputs, so the engine did not derive from it.'}
            </p>
          ))}
        </section>
      )}
    </>
  );
}

/**
 * ADR-0069 N20 — the note under the Exit value tile when the package states no exit cap.
 *
 * The tile itself stays an em dash: the engine computed no exit value, and a reference must never
 * stand in the tile as though it were one. The note says why it is empty and, on the paid tier, what
 * the CBRE range would imply, with every figure labelled "reference, not stated".
 */
export function ExitValueReferenceNote({ reference }: { reference: ExitCapReferenceView }) {
  const label = reference.label ?? 'reference, not stated';
  const range = reference.capRangeBps;
  const value = reference.exitValueRange;
  if (reference.kind !== 'reference') {
    return (
      <section className="tiles__withheld tiles__reference">
        <p>
          {/* The sentence already says the package states no exit cap; saying it twice is noise. */}
          <strong>Exit value was not computed.</strong>{' '}
          {reference.sentence ??
            'The package states no exit cap, and no exit cap reference could be shown for this deal.'}
        </p>
      </section>
    );
  }
  return (
    <section className="tiles__withheld tiles__reference">
      <p>
        <strong>Exit value was not computed.</strong> The package states no exit cap.{' '}
        {range && value ? (
          <>
            At GGP&apos;s reference range for {reference.market} (
            {percentFromRatio(range.low / 10_000, 2)} to {percentFromRatio(range.high / 10_000, 2)},{' '}
            {label}; inputs from the CBRE survey) the stated NOI implies{' '}
            {currencyCompact(value.atHigh)} to {currencyCompact(value.atLow)} ({label}). Not used in
            any score.
          </>
        ) : range ? (
          <>
            GGP&apos;s reference range for {reference.market}, built from CBRE survey inputs, is{' '}
            {percentFromRatio(range.low / 10_000, 2)} to {percentFromRatio(range.high / 10_000, 2)}{' '}
            ({label}). Not used in any score.
          </>
        ) : (
          <>
            A GGP exit cap reference for {reference.market}, built from CBRE survey inputs ({label}
            ), is on the full report.
          </>
        )}
      </p>
    </section>
  );
}

/**
 * Engine metric key → the label the grid shows, so the note names the tile a reader is looking at
 * rather than the identifier the engine uses internally.
 */
const TILE_LABELS: Record<string, string> = {
  costPerDoor: 'Cost / door',
  replacementReserves: 'Replacement reserve',
  reservePerUnit: 'Reserve / unit',
};

function passFlag(pass: boolean | null | undefined): 'pass' | 'fail' | undefined {
  if (pass === null || pass === undefined) return undefined;
  return pass ? 'pass' : 'fail';
}

// §3.3 heatmap + §3.4 returns bullet, together: "where does this deal land, and how robust is it."
function SensitivityCard({
  matrix,
  returns,
}: {
  matrix: IrrMatrix | null;
  returns: NonNullable<NonNullable<PremiumDealAnalysis['deterministic']>['spine']>['returns'];
}) {
  return (
    <section className="card">
      <h3 className="card__title">Sensitivity &amp; returns</h3>
      {returns && (
        <div className="returns">
          <ReturnsBullet
            projectIRR={returns.projectIRR}
            equityMultiple={returns.equityMultiple}
            hold={returns.holdYears}
          />
          {returns.loanFromStatedEquity === true && (
            <p className="muted">
              Levered returns size the loan from total cost less stated equity.
            </p>
          )}
        </div>
      )}
      {matrix && matrix.rows?.length ? (
        <>
          <div className="chart-scroll">
            <IrrHeatmapChart matrix={matrix} />
          </div>
          <BandLegend />
          {/* The grid's 36 figures, reachable without looking at pixels. */}
          <ChartTable {...irrMatrixTable(matrix)} />
        </>
      ) : null}
    </section>
  );
}

const LEGEND: { band: string; label: string }[] = [
  { band: 'strong', label: 'Strong ≥18%' },
  { band: 'adequate', label: 'Adequate ≥14%' },
  { band: 'marginal', label: 'Marginal ≥10%' },
  { band: 'weak', label: 'Weak <10%' },
];

function BandLegend() {
  return (
    <div className="legend">
      {LEGEND.map((l) => (
        <span className="legend__item" key={l.band}>
          <span className={`legend__swatch legend__swatch--${l.band}`} />
          {l.label}
        </span>
      ))}
      <span className="legend__item">
        <span className="legend__swatch legend__swatch--base" />
        Base case
      </span>
    </div>
  );
}

// §3.6 — lender hurdles as key/value rows + a hurdle bullet under the two gated metrics.
function LenderCard({ lender, narrative }: { lender: LenderSummary; narrative?: string }) {
  return (
    <section className="card">
      <h3 className="card__title">Lender credit summary</h3>
      <div className="kv">
        <Row
          k="Stabilized DSCR"
          v={multiple(lender.dscr, 2)}
          pass={lender.dscrPass}
          hint={`floor ${multiple(lender.dscrFloor, 2)}${
            lender.dscrFloorBasis ? ` — ${lender.dscrFloorBasis.label}` : ''
          }${
            // #1575 — the verdict rests on a reserve sized on a contradicted unit count.
            lender.dscrReserveWithheld && lender.dscrPass != null
              ? ' — result UNVERIFIED: the replacement reserve is sized on a unit count the documents contradict'
              : ''
          }`}
          bullet={{ value: lender.dscr, hurdle: lender.dscrFloor, pass: lender.dscrPass }}
        />
        {/* ADR-0069 N21 — informational context on the floor (Indiana's published coverage ranges). */}
        {lender.dscrFloorBasis?.note && <p className="kv-hint">{lender.dscrFloorBasis.note}</p>}
        <Row
          k="Construction LTC"
          v={percentFromRatio(lender.constructionLTC, 1)}
          pass={lender.ltcPass}
          hint={
            lender.ltcCap !== undefined ? `cap ${percentFromRatio(lender.ltcCap, 0)}` : undefined
          }
          bullet={{ value: lender.constructionLTC, hurdle: lender.ltcCap, pass: lender.ltcPass }}
        />
        <Row k="Debt yield" v={percentFromRatio(lender.debtYield, 2)} />
        <Row k="Refi LTV" v={percentFromRatio(lender.refiLTV, 1)} />
        <Row k="Stabilized value" v={currency(lender.stabilizedValue)} />
      </div>
      {/*
        #438 — the three loan-derived figures above share one loan amount, and the construction view
        and the takeout view do not want the same facility. Rendered next to the numbers because a
        caveat a reader has to go looking for is the silent version with extra steps.
      */}
      {lender.loanBasis === 'unqualified' && (
        <p className="card__note">
          One loan amount feeds all three loan figures. Construction LTC wants the construction
          loan; debt yield and refi LTV want the permanent loan. If this deal carries both, one view
          is measured against the other&rsquo;s loan — confirm the loan behind the LTC before
          relying on it.
        </p>
      )}
      {/* The bullet bars encode pass/fail as position + colour; this says it in words. */}
      <ChartTable {...lenderTable(lender)} />
      {narrative && <Markdown body={narrative} className="card__prose" />}
    </section>
  );
}

function Row({
  k,
  v,
  pass,
  hint,
  bullet,
}: {
  k: string;
  v: string;
  pass?: boolean | null;
  hint?: string;
  bullet?: {
    value: number | null | undefined;
    hurdle: number | null | undefined;
    pass?: boolean | null;
  };
}) {
  return (
    <div className="kv-row">
      <span className="k">
        {k}
        {hint && <span className="kv-hint"> · {hint}</span>}
      </span>
      <span className="v">
        {v}
        {pass !== undefined && pass !== null && (
          <span className={`pill pill--${pass ? 'pass' : 'fail'}`}>{pass ? 'pass' : 'fail'}</span>
        )}
      </span>
      {bullet && bullet.hurdle != null && (
        <div className="kv-bullet">
          <HurdleBar value={bullet.value} hurdle={bullet.hurdle} pass={bullet.pass} />
        </div>
      )}
    </div>
  );
}

// §3.7 — completeness donut + the badge/score + the flags list.
/** Plain-language names for the outside sources — a reader should not have to know our field names. */
const EXTERNAL_CHECK_LABELS: Record<string, string> = {
  landBasis: 'Land cost against the county record',
  siteContext: 'Site context — flood, demographics, labour, permits, rents',
  capRateSpread: 'Exit cap rate against benchmark rates',
  sponsorFilings: 'Sponsor name against SEC filings',
  reconciliation: 'Figures cross-checked between documents',
  // ⚠️ Required, not cosmetic: `ExternalChecksCard` filters the run list against THESE keys, so a
  // check with no label here is declared wired by the bundle and then silently omitted from the very
  // card that exists to say what was checked.
  propertyTax: 'Proforma property tax against the county tax rate',
};

/**
 * What this deal was — and was not — checked against outside its own documents.
 *
 * ⚠️ Why this card exists: every external check is EVIDENCE and never a gate, so a flag that is off, a
 * resolver that threw, a deal with no address, and a clean result all produced the same thing — no panel.
 * A deal entered on the form (which runs none of these) rendered exactly like one verified against the
 * county record. This states which happened.
 */
function ExternalChecksCard({
  ran,
  notChecked = [],
}: {
  ran: readonly string[];
  notChecked?: readonly string[];
}) {
  const known = Object.keys(EXTERNAL_CHECK_LABELS);
  const didRun = known.filter((k) => ran.includes(k) && !notChecked.includes(k));
  const declined = known.filter((k) => ran.includes(k) && notChecked.includes(k));
  const didNot = known.filter((k) => !ran.includes(k));
  return (
    <section className="card">
      <h3 className="card__title">Outside checks</h3>
      {didRun.length === 0 ? (
        <p className="card__prose">
          This deal was read from what was submitted and{' '}
          <strong>not checked against any outside source</strong>. Nothing here has been
          corroborated against the county record, benchmark rates, or filings. Submit the
          deal&rsquo;s documents to have those checks run.
        </p>
      ) : (
        <p className="card__prose">
          Checked against outside sources where the deal gave us enough to look it up. A check that
          ran and found nothing worth flagging shows no panel of its own.
        </p>
      )}
      <div className="kv">
        {didRun.map((k) => (
          <div className="kv-row" key={k}>
            <span className="k">{EXTERNAL_CHECK_LABELS[k]}</span>
            <span className="v">
              <span className="pill pill--pass">checked</span>
            </span>
          </div>
        ))}
        {declined.map((k) => (
          <div className="kv-row" key={k}>
            <span className="k">{EXTERNAL_CHECK_LABELS[k]}</span>
            <span className="v">
              <span className="pill">not checked</span>
            </span>
          </div>
        ))}
        {didNot.map((k) => (
          <div className="kv-row" key={k}>
            <span className="k">{EXTERNAL_CHECK_LABELS[k]}</span>
            <span className="v">
              <span className="pill">not run</span>
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * The data-quality card. `narrativeFlags` are the ADR-0069 N20 narrative-number flags: they render in
 * the same list as the data-quality flags, at their `reconcile` severity, but they are NOT part of the
 * report's score or its summary count. They are about our own prose, not the sponsor's package, so the
 * summary sentence above them stays the report's own.
 */
export function DataQualityCard({
  dq,
  narrativeFlags = [],
}: {
  dq: DataQualityReport;
  narrativeFlags?: readonly NarrativeNumberFlagView[];
}) {
  const present = dq.presentFields?.length ?? 0;
  const missing = dq.missingFields?.length ?? 0;
  const total = present + missing;
  return (
    <section className="card">
      <h3 className="card__title">
        Data quality
        {dq.badge && <span className={`pill pill--badge pill--${dq.badge}`}>{dq.badge}</span>}
        {dq.score !== undefined && <span className="card__score">{dq.score}/100</span>}
      </h3>
      <div className="dq">
        <div className="dq__donut">
          {total > 0 ? (
            <CompletenessDonut computed={present} total={total} label="fields" />
          ) : (
            dq.score !== undefined && (
              <CompletenessDonut computed={dq.score} total={100} label="quality" />
            )
          )}
        </div>
        <div className="dq__body">
          {dq.summary && <p className="card__prose">{dq.summary}</p>}
          {missing > 0 && <p className="card__note">Missing: {dq.missingFields?.join(', ')}.</p>}
          {((dq.flags && dq.flags.length > 0) || narrativeFlags.length > 0) && (
            <ul className="flags">
              {(dq.flags ?? []).map((f, i) => (
                <li key={`${f.field}-${i}`} className={`flag flag--${f.severity}`}>
                  <span className="flag__sev">{f.severity}</span> {f.message}
                </li>
              ))}
              {narrativeFlags.map((f, i) => (
                <li
                  key={`narrative-${f.field}-${i}`}
                  className={`flag flag--${f.severity}`}
                  data-narrative-number={f.field}
                >
                  <span className="flag__sev">{f.severity}</span> {f.message}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

/**
 * Drop a heading that is the FIRST thing in model-authored prose.
 *
 * The modules open their narratives with their own `## Rationale` / `## IC memorandum`, and the card
 * already carries a title — so the report rendered two stacked headings saying the same thing, one of
 * them (the model's) usually the less informative of the pair. Only a LEADING heading is removed;
 * every later heading is the model's structure and stays.
 */
export function stripLeadingHeading(body: string): string {
  return body.replace(/^\s*#{1,4}[ \t]+\S[^\n]*(?:\n+|$)/, '');
}

/**
 * What a reader is told when a module was asked to run and failed.
 *
 * ⚠️ The copy differs per module BECAUSE THE CONSEQUENCE DIFFERS, and getting that wrong would be a
 * false reassurance on a client-facing surface:
 *
 *  • Sensitivity / Lender / IC Memo are **narrative**. Sensitivity and Lender render engine-computed
 *    figures with prose over them, and the IC Memo is BOUND to the recommendation rather than an input
 *    to it — so the 0–100 score is genuinely unchanged, and saying so is true and worth saying.
 *  • **Sponsor is NOT** — `applySponsorScore` normally replaces the Scorecard's own sponsor judgment
 *    with the module's. If the module failed, the Scorecard's fallback judgment stands, so the score is
 *    valid but composed differently than a clean run. Claiming "unaffected" there would be a lie.
 */
const MODULE_UNAVAILABLE: Record<string, { label: string; consequence: string }> = {
  sensitivity: {
    label: 'Sensitivity commentary',
    consequence: 'The scenario grid and the 0–100 score are engine-computed and unchanged.',
  },
  lender: {
    label: 'Lender commentary',
    consequence: 'The credit metrics and the 0–100 score are engine-computed and unchanged.',
  },
  icMemo: {
    label: 'IC memorandum',
    consequence: 'The 0–100 score and its recommendation are unchanged — the memo reports them.',
  },
  construction: {
    label: 'Construction risk commentary',
    // Accurate for the same reason as Lender: the CSI decomposition is engine-computed and the
    // Scorecard never reads it (`runScorecard` takes the spine and the deal, not the decomposition),
    // so a failed narrative cannot move the score. ⚠️ Verified rather than assumed — the Sponsor entry
    // above exists because that claim is FALSE there.
    consequence: 'The CSI decomposition and the 0–100 score are engine-computed and unchanged.',
  },
  sponsor: {
    label: 'Sponsor & team risk',
    consequence:
      'Sponsor & Execution was scored from the Scorecard’s own assessment rather than the ' +
      'dedicated sponsor review, so this run’s score is valid but composed differently.',
  },
};

/**
 * Why the rent-vs-market check did not run.
 *
 * ⚠️ A DIFFERENT THING FROM `ModuleUnavailable`, and the distinction is the point. That card explains a
 * module that was asked to run and FAILED. This explains a check that could not run because an INPUT
 * was absent — nothing broke, and the report should not imply anything did.
 *
 * It exists because the engine already required it and nothing discharged it: `rentPosition`'s own
 * docstring says "null is a real answer here and the caller must say so… silence would let a reader
 * take 'no rent finding' for 'the rent was checked and was fine'." The field rode the record and
 * rendered nowhere — the disclosure existed and no reader could see it, which is the same defect as
 * not having written it.
 */
const RENT_CHECK_NOT_RUN: Record<string, string> = {
  'no-comps':
    'This package carried no market rent comps, so there is no band to place the deal’s rent against.',
  'no-class':
    'No property class could be established for this deal, and the comp band is per class — so there is nothing to compare against.',
  'class-from-own-rent':
    'No class was stated, so the class came from this deal’s own rent per square foot. Its band was chosen by the rent it would be checking, so the comparison would prove nothing and was not made.',
  'unusable-band':
    'Market comps were present but did not produce a usable band, so no comparison was made.',
  'no-deal-rent':
    'The package did not state an average rent, nor the gross potential rent and unit count needed to derive one.',
};

function RentCheckNotRun({ reason }: { reason: string }) {
  const body = RENT_CHECK_NOT_RUN[reason];
  // An unrecognised reason renders nothing rather than a shrug — a note that says "unavailable for an
  // unknown reason" is worse than no note, and a new reason should be added here deliberately.
  if (!body) return null;
  return (
    <section className="card">
      <h3 className="card__title">Rent vs market — not checked</h3>
      <div className="md card__prose">
        <p>{body}</p>
        <p>
          This is a gap in what the documents stated, not a finding about the deal, and not a
          failure. The rest of the underwrite is unaffected.
        </p>
      </div>
    </section>
  );
}

function ModuleUnavailable({ modules }: { modules: string[] }) {
  const known = modules.filter((m) => m in MODULE_UNAVAILABLE);
  if (known.length === 0) return null;
  return (
    <section className="card">
      <h3 className="card__title">Not available for this run</h3>
      {/*
        `md card__prose`, exactly as `Narrative` does — NOT `card__prose` alone. `.card__prose` styles
        the CONTAINER as a text block; the child-paragraph rhythm lives in `.md p { margin: 0 0 12px }`
        plus `.md > :first-child/:last-child` margin resets. With bare `<p>` and no `.md`, these
        paragraphs would take the browser's default 1em margins — a different vertical rhythm from every
        other card, and a stray gap under the title. Invisible to an SSR test, which sees the markup and
        not the cascade.
      */}
      <div className="md card__prose">
        {known.map((m) => {
          const entry = MODULE_UNAVAILABLE[m] as { label: string; consequence: string };
          return (
            <p key={m}>
              <strong>{entry.label}</strong> could not be generated for this run.{' '}
              {entry.consequence}
            </p>
          );
        })}
        <p>
          Re-running the analysis will attempt it again. The underlying figures were not affected —
          a module is reported as unavailable rather than omitted so that nothing appears to be
          missing by design.
        </p>
      </div>
    </section>
  );
}

function Narrative({ title, body }: { title: string; body?: string }) {
  if (!body) return null;
  const prose = stripLeadingHeading(body);
  if (!prose.trim()) return null;
  return (
    <section className="card">
      <h3 className="card__title">{title}</h3>
      <Markdown body={prose} className="card__prose" />
    </section>
  );
}
